// src/components/home/QuickServices.jsx

import React from "react";
import {
  Car,
  Plane,
  Route,
  MapPin,
  ShieldAlert,
  ArrowUpRight,
} from "lucide-react";

const services = [
  {
    icon: Car,
    title: "Car Rental",
    subtitle: "With Driver",
    description: "Comfortable vehicles for daily travel.",
  },
  {
    icon: Plane,
    title: "Airport Transfer",
    subtitle: "24/7 Service",
    description: "Reliable airport pickup and drop-off.",
  },
  {
    icon: Route,
    title: "Outstation Trips",
    subtitle: "Anywhere",
    description: "Comfortable travel to your destination.",
  },
  {
    icon: MapPin,
    title: "Local Travel",
    subtitle: "City Rides",
    description: "Convenient rides for local requirements.",
  },
  {
    icon: ShieldAlert,
    title: "Emergency Service",
    subtitle: "Available 24/7",
    description: "Need a vehicle urgently? Contact NJV.",
  },
];

const QuickServices = () => (
  <section className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8">

    {/* =====================================================
        SECTION HEADER
    ===================================================== */}

    <div className="mb-5 flex items-end justify-between gap-4 sm:mb-7">

      <div>
        <span
          className="
            text-[10px]
            font-bold
            uppercase
            tracking-[0.2em]
            text-[#00A260]

            sm:text-[11px]
          "
        >
          Our Services
        </span>

        <h2
          className="
            mt-1.5
            text-xl
            font-extrabold
            tracking-tight
            text-[#061A3A]

            sm:mt-2
            sm:text-3xl
          "
        >
          Travel made simple
        </h2>

        <p
          className="
            mt-1.5
            max-w-xl
            text-xs
            leading-5
            text-[#64748B]

            sm:mt-2
            sm:text-sm
            sm:leading-6
          "
        >
          Reliable vehicles and travel services for every journey.
        </p>
      </div>

      {/* Desktop icon */}
      <div
        className="
          hidden
          h-9
          w-9
          shrink-0
          items-center
          justify-center
          rounded-xl
          bg-[#F1F5F3]
          text-[#00A260]

          sm:flex
          sm:h-10
          sm:w-10
        "
      >
        <Car className="h-4 w-4 sm:h-5 sm:w-5" />
      </div>
    </div>


    {/* =====================================================
        SERVICE CARDS
    ===================================================== */}

    <div
      className="
        flex
        gap-3
        overflow-x-auto
        pb-3

        [scrollbar-width:none]
        [&::-webkit-scrollbar]:hidden

        sm:grid
        sm:grid-cols-2
        sm:gap-4
        sm:overflow-visible
        sm:pb-0

        lg:grid-cols-5
      "
    >
      {services.map(
        ({ icon: Icon, title, subtitle, description }, index) => (
          <article
            key={title}
            className="
              group
              relative
              w-[205px]
              min-w-[205px]
              shrink-0
              overflow-hidden
              rounded-2xl
              border
              border-[#E7ECE9]
              bg-white
              px-4
              py-4

              shadow-[0_4px_18px_rgba(6,26,58,0.04)]

              transition-all
              duration-300

              hover:-translate-y-0.5
              hover:shadow-[0_10px_25px_rgba(6,26,58,0.07)]

              sm:w-auto
              sm:min-w-0
              sm:rounded-3xl
              sm:p-5
            "
          >

            {/* =================================================
                SMALL TOP ACCENT
            ================================================= */}

            <div
              className="
                absolute
                left-0
                right-0
                top-0
                h-[2px]
                bg-[#00A260]/70
              "
            />


            {/* =================================================
                NUMBER
            ================================================= */}

            <span
              className="
                absolute
                right-3
                top-3
                text-[9px]
                font-bold
                text-[#CBD5E1]

                sm:right-4
                sm:top-4
                sm:text-[10px]
              "
            >
              0{index + 1}
            </span>


            {/* =================================================
                ICON
            ================================================= */}

            <div
              className="
                flex
                h-10
                w-10
                items-center
                justify-center
                rounded-xl
                bg-[#F1F8F5]
                text-[#00A260]

                transition-transform
                duration-300

                group-hover:scale-105

                sm:h-12
                sm:w-12
                sm:rounded-2xl
              "
            >
              <Icon className="h-[18px] w-[18px] sm:h-5 sm:w-5" />
            </div>


            {/* =================================================
                CONTENT
            ================================================= */}

            <div className="mt-3 sm:mt-5">

              <h3
                className="
                  text-sm
                  font-extrabold
                  leading-5
                  text-[#061A3A]

                  sm:text-base
                "
              >
                {title}
              </h3>

              <p
                className="
                  mt-0.5
                  text-[10px]
                  font-bold
                  text-[#00A260]

                  sm:text-xs
                "
              >
                {subtitle}
              </p>

              <p
                className="
                  mt-2
                  line-clamp-2
                  text-[10px]
                  leading-4
                  text-[#64748B]

                  sm:mt-3
                  sm:text-xs
                  sm:leading-5
                "
              >
                {description}
              </p>
            </div>


            {/* =================================================
                BOTTOM
            ================================================= */}

            
          </article>
        )
      )}
    </div>


    {/* =====================================================
        MOBILE SWIPE HINT
    ===================================================== */}

    <div className="mt-2.5 flex items-center justify-center gap-2 sm:hidden">

      <span className="h-1 w-5 rounded-full bg-[#00A260]" />

      <span className="h-1 w-1.5 rounded-full bg-[#CBD5E1]" />

      <span className="text-[9px] font-medium text-[#94A3B8]">
        Swipe to explore
      </span>

    </div>
  </section>
);

export default QuickServices;